import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Plus, Tag, Heart, Disc3, Info, X, Search, BarChart3, LayoutGrid, List } from "lucide-react";
import { z } from "zod";

import { AppShell } from "@/components/AppShell";
import { PageHero, PageHeroStat } from "@/components/PageHero";
import { PAGE_CONTAINER } from "@/components/PageContainer";
import { AddToCollectionMenu } from "@/components/AddToCollectionMenu";
import { usePlanAccess } from "@/hooks/usePlanAccess";
import { RandomizeRecordFab } from "@/components/collection/RandomizeRecordFab";

import { EmptyState } from "@/components/ui/empty-state";
import { AlbumCard } from "@/components/AlbumCard";
import { SegmentedTabs } from "@/components/ui/segmented-tabs";
import { SubNavBar } from "@/components/ui/sub-nav";
import { AlbumDetailsDialog } from "@/components/AlbumDetailsDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { CollectionBoard } from "@/components/collection/CollectionBoard";
import { useIsMobile } from "@/hooks/use-mobile";

import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  listMyCollection,
  listMyWishlist,
  type CollectionItem,
} from "@/lib/collection.functions";

type Tab = "collection" | "wishlist" | "listed";

const tabSchema = z.object({
  tab: z.enum(["collection", "wishlist", "listed"]).optional(),
  /** Onboarding em destaque (ex.: vindo do banner "Deixa a sorte decidir"). */
  hl: z.enum(["dice"]).optional(),
});

export const Route = createFileRoute("/collection")({
  validateSearch: tabSchema,
  head: () => ({ meta: [{ title: "Minha coleção — Viny Club" }] }),
  component: CollectionPage,
});

function CollectionPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/collection" });
  const tab: Tab = (search.tab as Tab) ?? "collection";
  const setTab = (t: Tab) =>
    navigate({ search: () => ({ tab: t === "collection" ? undefined : t }), replace: true });


  const collectionFn = useServerFn(listMyCollection);
  const wishlistFn = useServerFn(listMyWishlist);
  const { user, loading: authLoading } = useCurrentUser();
  const isMobile = useIsMobile();

  const collectionQ = useQuery({
    queryKey: ["my-collection", user?.id ?? null],
    queryFn: () => collectionFn(),
    enabled: !!user,
    retry: false,
  });
  const wishlistQ = useQuery({
    queryKey: ["my-wishlist", user?.id ?? null],
    queryFn: () => wishlistFn(),
    enabled: !!user,
    retry: false,
  });

  const isUnauthed =
    (!authLoading && !user) ||
    collectionQ.error?.message?.includes("Unauthorized") ||
    wishlistQ.error?.message?.includes("Unauthorized");

  const owned = collectionQ.data ?? [];
  const wished = wishlistQ.data ?? [];
  const listed = useMemo(() => owned.filter((a) => a.listing !== "none"), [owned]);

  const totalPaid = owned.reduce((s, a) => s + (a.paid_price ?? 0), 0);

  const [query, setQuery] = useState("");
  const [view, setView] = useState<"grid" | "list">(() => {
    if (typeof window === "undefined") return "grid";
    return window.localStorage.getItem("viny:collection-view") === "list" ? "list" : "grid";
  });
  useEffect(() => {
    try {
      window.localStorage.setItem("viny:collection-view", view);
    } catch {
      /* ignore */
    }
  }, [view]);

  const listedFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return listed;
    return listed.filter(
      (a) => a.title.toLowerCase().includes(q) || a.artist.toLowerCase().includes(q),
    );
  }, [listed, query]);

  const plan = usePlanAccess();

  const limitBanner = null;

  const cta = (
    <AddToCollectionMenu
      label={tab === "wishlist" ? "Adicionar à wishlist" : "Adicionar disco"}
      defaultTarget={tab === "wishlist" ? "wishlist" : "owned"}
    />
  );


  const askedTotal = useMemo(
    () => listed.reduce((s, a) => s + (a.sale_price ?? a.price_brl ?? 0), 0),
    [listed],
  );

  const header =
    tab === "wishlist"
      ? {
          eyebrow: "Coleção",
          title: "Meus interesses",
          subtitle:
            "Os discos que você quer caçar. Avisamos assim que aparecerem à venda na plataforma.",
          heroStats: [
            { label: "Na lista", value: String(wished.length) },
            { label: "Alertas ativos", value: String(wished.length) },
          ],
        }
      : tab === "listed"
        ? {
            eyebrow: "Coleção",
            title: "Meus anúncios",
            subtitle: "Os discos que você colocou à venda ou para troca no marketplace.",
            heroStats: [
              { label: "Anunciados", value: String(listed.length) },
              ...(askedTotal > 0
                ? [{ label: "Total pedido", value: `R$ ${askedTotal.toFixed(0)}` }]
                : []),
            ],
          }
        : {
            eyebrow: "Coleção",
            title: "Minha coleção",
            subtitle: "Todos os discos que você já catalogou, do jeito que você gosta.",
            heroStats: [
              { label: "Discos", value: String(owned.length) },
              ...(totalPaid > 0
                ? [{ label: "Investido", value: `R$ ${totalPaid.toFixed(0)}` }]
                : []),
            ],
          };



  // Acesso direto aos insights — botão compacto na linha de busca, sem o card grande.
  const insightsBtn =
    tab === "collection" && owned.length > 0 ? (
      <Link
        to="/insights"
        className="inline-flex h-12 shrink-0 items-center gap-2 rounded-full border border-[color:var(--hairline)] bg-surface/60 px-3 text-[13px] font-semibold text-foreground transition-colors hover:bg-surface sm:h-11 sm:px-4"
      >
        <BarChart3 className="h-4 w-4 shrink-0 text-[color:var(--action)]" />
        <span className="hidden sm:inline">Insights</span>
      </Link>
    ) : null;

  const searchRow = (
    <div className="flex items-center gap-2.5">
      <div className="relative min-w-0 flex-1">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Buscar na coleção"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={`h-12 w-full rounded-full border-border bg-surface/60 pl-11 text-base shadow-sm backdrop-blur-sm transition-colors focus-visible:bg-surface focus-visible:ring-1 focus-visible:ring-primary sm:h-11 sm:text-sm ${
            query ? "pr-11" : "pr-4 sm:pr-11"
          }`}
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground"
            aria-label="Limpar busca"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {insightsBtn}
      <div className="flex shrink-0 items-center gap-1 rounded-full border border-[color:var(--hairline)] bg-surface/40 p-1 lg:hidden">
        {(
          [
            ["grid", LayoutGrid, "Grade"],
            ["list", List, "Lista"],
          ] as const
        ).map(([mode, Icon, label]) => (
          <button
            key={mode}
            type="button"
            aria-label={label}
            aria-pressed={view === mode}
            onClick={() => setView(mode)}
            className={
              "inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors " +
              (view === mode
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground")
            }
          >
            <Icon className="h-4 w-4" />
          </button>
        ))}
      </div>
    </div>
  );

  const tabItems = [
    { value: "collection" as Tab, label: "Discos", count: owned.length },
    { value: "wishlist" as Tab, label: "Interesses", count: wished.length },
    { value: "listed" as Tab, label: "Anúncios", count: listed.length },
  ];

  return (
    <AppShell>
      {/* Desktop: abas acima do título, igual ao Explorar. */}
      <div className="mx-auto hidden w-full max-w-[1400px] px-5 pb-3 pt-6 lg:flex">
        <SegmentedTabs<Tab>
          layoutId="collection-tabs-desktop"
          ariaLabel="Seções da coleção"
          size="sm"
          className="h-11 rounded-2xl border-foreground/10 bg-surface/70 p-1"
          items={tabItems}
          value={tab}
          onChange={setTab}
        />
      </div>

      {/* Mobile: abas fixas abaixo do menu, igual ao Explorar. */}
      <SubNavBar className="mb-4 lg:hidden">
        <SegmentedTabs<Tab>
          layoutId="collection-tabs-mobile"
          ariaLabel="Seções da coleção"
          fullWidth
          size="sm"
          className="h-11 rounded-2xl border-foreground/10 bg-surface/70 p-1"
          items={tabItems}
          value={tab}
          onChange={setTab}
        />
      </SubNavBar>

      <PageHero
        eyebrow={header.eyebrow}
        title={header.title}
        subtitle={isMobile ? undefined : header.subtitle}
        actions={!isMobile && tab !== "listed" ? <div className="hidden lg:block">{cta}</div> : null}
        aside={
          !isMobile ? (
            <div className="grid grid-cols-2 gap-3">
              {header.heroStats.map((s) => (
                <PageHeroStat key={s.label} label={s.label} value={s.value} />
              ))}
            </div>
          ) : null
        }
      />


      {limitBanner ? <div className={PAGE_CONTAINER}>{limitBanner}</div> : null}

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className={PAGE_CONTAINER}>
        <div className="mt-6 lg:mt-7">{searchRow}</div>

        <TabsContent value="collection" className="mt-7 pb-16 lg:mt-8">
          {isMobile ? (
            <MobileBoard
              items={owned}
              query={query}
              view={view}
              isLoading={collectionQ.isLoading}
              isUnauthed={!!isUnauthed}
              emptyTitle="Coleção vazia"
              emptySubtitle="Adicione seu primeiro disco para começar."
            />
          ) : (
            <CollectionBoard
              kind="owned"
              items={owned}
              isLoading={collectionQ.isLoading}
              isUnauthed={!!isUnauthed}
              query={query}
              emptyTitle="Coleção vazia"
              emptySubtitle="Adicione seu primeiro disco para começar."
            />
          )}
        </TabsContent>

        <TabsContent value="wishlist" className="mt-7 pb-16 lg:mt-8">
          {isMobile ? (
            <MobileBoard
              items={wished}
              query={query}
              view={view}
              isLoading={wishlistQ.isLoading}
              isUnauthed={!!isUnauthed}
              emptyTitle="Interesse vazio"
              emptySubtitle="Adicione discos que você quer caçar."
            />
          ) : (
            <CollectionBoard
              kind="wishlist"
              items={wished}
              isLoading={wishlistQ.isLoading}
              isUnauthed={!!isUnauthed}
              query={query}
              emptyTitle="Interesse vazio"
              emptySubtitle="Adicione discos que você quer caçar — avisamos quando aparecerem."
            />
          )}
        </TabsContent>

        <TabsContent value="listed" className="mt-7 pb-16 lg:mt-8">
          {isMobile ? (
            <MobileBoard
              items={listedFiltered}
              query=""
              view={view}
              isLoading={collectionQ.isLoading}
              isUnauthed={!!isUnauthed}
              showPrice
              emptyTitle={
                query.trim() ? `Nenhum resultado para "${query.trim()}"` : "Nenhum anúncio ativo"
              }
              emptySubtitle={
                query.trim()
                  ? "Tente outro termo de busca."
                  : "Marque discos como À venda ou Para troca."
              }
            />
          ) : (
            <Grid
              items={listedFiltered}
              isLoading={collectionQ.isLoading}
              isUnauthed={!!isUnauthed}
              emptyTitle={query.trim() ? `Nenhum resultado para "${query.trim()}"` : "Nenhum anúncio ativo"}
              emptySubtitle={query.trim() ? "Tente outro termo de busca." : "Marque discos da sua coleção como À venda ou Para troca."}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* FAB roxo de adicionar — só mobile, acima da tab bar.
          Portal no body: a animação de entrada do <main> cria um containing
          block que quebraria o `position: fixed`. */}
      {tab !== "listed" && typeof document !== "undefined"
        ? createPortal(
            <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+92px)] right-4 z-30 flex flex-col items-center gap-3 lg:hidden">
              {tab === "collection" && isMobile ? (
                <RandomizeRecordFab items={owned} highlight={search.hl === "dice"} />
              ) : null}
              <AddToCollectionMenu
                fab
                label={tab === "wishlist" ? "Adicionar à wishlist" : "Adicionar disco"}
                defaultTarget={tab === "wishlist" ? "wishlist" : "owned"}
              />
            </div>,
            document.body,
          )
        : null}

      {/* Randomizar disco — desktop (somente na aba Minha coleção). */}
      {tab === "collection" && !isMobile && typeof document !== "undefined"
        ? createPortal(
            <div className="fixed bottom-8 right-8 z-30 hidden lg:block">
              <RandomizeRecordFab items={owned} highlight={search.hl === "dice"} />
            </div>,
            document.body,
          )
        : null}
    </AppShell>
  );
}


/** Board mobile minimalista: grade de capas ou lista compacta. */
function MobileBoard({
  items,
  query,
  view,
  isLoading,
  isUnauthed,
  showPrice = false,
  emptyTitle,
  emptySubtitle,
}: {
  items: CollectionItem[];
  query: string;
  view: "grid" | "list";
  isLoading: boolean;
  isUnauthed: boolean;
  showPrice?: boolean;
  emptyTitle: string;
  emptySubtitle: string;
}) {
  const [selected, setSelected] = useState<CollectionItem | null>(null);
  const q = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      q
        ? items.filter(
            (a) =>
              a.title.toLowerCase().includes(q) || a.artist.toLowerCase().includes(q),
          )
        : items,
    [items, q],
  );

  if (isUnauthed) {
    return (
      <Empty
        title="Entre para ver sua coleção"
        cta={
          <Link
            to="/auth"
            className="rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"
          >
            Entrar
          </Link>
        }
      />
    );
  }
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="aspect-square animate-pulse rounded-2xl bg-surface/40" />
        ))}
      </div>
    );
  }
  if (visible.length === 0) {
    return (
      <Empty
        title={q ? `Nenhum resultado para "${query.trim()}"` : emptyTitle}
        subtitle={q ? undefined : emptySubtitle}
        cta={null}
      />
    );
  }

  return (
    <>
      {view === "list" ? (
        <div className="flex flex-col gap-1">
          {visible.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setSelected(a)}
              className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-left text-[14px] leading-tight transition-colors active:bg-surface/60"
            >
              <img
                src={a.cover_url ?? ""}
                alt={a.title}
                loading="lazy"
                decoding="async"
                className="h-7 w-7 shrink-0 rounded-[3px] object-cover ring-1 ring-[color:var(--hairline)]"
              />
              <span className="min-w-0 flex-1 truncate">{a.title}</span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {showPrice && (a.sale_price ?? a.price_brl) != null
                  ? `R$ ${a.sale_price ?? a.price_brl}`
                  : (a.year ?? "—")}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-3 gap-y-5">
          {visible.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setSelected(a)}
              className="group block w-full min-w-0 text-left"
            >
              <div className="relative aspect-square overflow-hidden rounded-2xl bg-surface ring-1 ring-[color:var(--hairline)] transition-transform duration-200 active:scale-[0.97]">
                <img
                  src={a.cover_url ?? ""}
                  alt={a.title}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
                {showPrice && (a.sale_price ?? a.price_brl) != null && (
                  <div className="absolute right-2 top-2 rounded-full bg-background/80 px-2 py-0.5 text-[11px] font-semibold backdrop-blur-sm">
                    R$ {a.sale_price ?? a.price_brl}
                  </div>
                )}
              </div>
              <div className="mt-2 truncate text-[13px] font-medium tracking-tight">{a.title}</div>
              <div className="truncate text-[11px] text-muted-foreground">{a.artist}</div>
            </button>
          ))}
        </div>
      )}

      <AlbumDetailsDialog
        album={selected}
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
      />
    </>
  );
}

function Grid({
  items,
  isLoading,
  isUnauthed,
  emptyTitle,
  emptySubtitle,
}: {
  items: CollectionItem[];
  isLoading: boolean;
  isUnauthed: boolean;
  emptyTitle: string;
  emptySubtitle: string;
}) {
  const [selected, setSelected] = useState<CollectionItem | null>(null);

  if (isUnauthed) {
    return (
      <Empty
        title="Faça login para acessar sua coleção"
        cta={
          <Link
            to="/auth"
            className="rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"
          >
            Entrar
          </Link>
        }
      />
    );
  }
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="aspect-square animate-pulse rounded-xl bg-surface/40" />
        ))}
      </div>
    );
  }
  if (items.length === 0) {
    return <Empty title={emptyTitle} subtitle={emptySubtitle} cta={null} />;
  }
  return (
    <>
      <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
        {items.map((a, i) => (
          <AlbumCard key={a.id} album={a} index={i} onClick={() => setSelected(a)} />
        ))}
      </div>
      <AlbumDetailsDialog
        album={selected}
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
      />
    </>
  );
}

function WishlistBanner() {
  const KEY = "wishlist-alert-banner-dismissed";
  const [open, setOpen] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem(KEY)) setOpen(true);
    } catch {}
  }, []);
  if (!open) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6 flex items-start gap-3 rounded-2xl border border-primary/20 bg-primary/10 px-5 py-4"
    >
      <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
      <div className="flex-1 text-sm leading-relaxed text-foreground">
        <span className="font-semibold">Fique de olho:</span> você recebe alertas
        automáticos quando esses álbuns aparecerem à venda na plataforma.
      </div>
      <button
        onClick={() => {
          try {
            localStorage.setItem(KEY, "1");
          } catch {}
          setOpen(false);
        }}
        className="shrink-0 rounded-lg p-1 text-muted-foreground hover:bg-primary/20 hover:text-foreground"
        aria-label="Fechar aviso"
      >
        <X className="h-4 w-4" />
      </button>
    </motion.div>
  );
}




function Stat({
  label,
  value,
  small = false,
}: {
  label: string;
  value: string | number;
  small?: boolean;
}) {
  return (
    <div>
      <div className={`font-display font-bold leading-none ${small ? "text-2xl" : "text-3xl lg:text-4xl"}`}>
        {value}
      </div>
      <div className="mt-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

const PrimaryButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { icon: typeof Plus; children: React.ReactNode }
>(({ icon: Icon, children, className, ...props }, ref) => (
  <button
    ref={ref}
    {...props}
    className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-[0_8px_30px_-8px_hsl(var(--primary)/0.5)] transition-transform hover:scale-105"
  >
    <Icon className="h-4 w-4" /> {children}
  </button>
));
PrimaryButton.displayName = "PrimaryButton";

function Empty({
  title,
  subtitle,
  cta,
}: {
  title: string;
  subtitle?: string;
  cta: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-xl">
      <EmptyState
        title={title}
        description={subtitle}
        icons={[<Disc3 key="d" />, <Heart key="h" />, <Tag key="t" />]}
      />
      {cta && <div className="mt-4 flex justify-center">{cta}</div>}
    </div>
  );
}

